/**
 * PromChallengeLeaderboardPage — /prom/:slug/challenges/:challengeId
 *
 * Single challenge experience:
 *   • Title + rank-context micro-prompt
 *   • Submit-entry CTA (camera/upload — auto-submits, no form)
 *   • Live leaderboard ranked by votes
 *   • Tap any entry to vote (1 vote per anon_id per challenge)
 *   • "Get more votes" share button
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Trophy, Crown, Flame, Camera, ArrowLeft, ShareNetwork, CheckCircle, Sparkle } from '@phosphor-icons/react';
import { trackGrowth, getAnonId } from '../lib/growthTrack';
import UpsellBanner from '../components/UpsellBanner';
import { getMediaUrl } from '../lib/media';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PromChallengeLeaderboardPage() {
  const { slug, challengeId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const leaderboardTopRef = useRef(null);
  const [event, setEvent] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const anonId = getAnonId();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ev = await axios.get(`${API}/events/slug/${slug}`);
        if (cancelled) return;
        setEvent(ev.data);
        await refresh();
      } catch (e) {
        if (!cancelled) setError('Could not load this challenge.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, challengeId]);

  const refresh = async () => {
    const res = await axios.get(`${API}/challenges/${challengeId}/leaderboard`, {
      params: { anon_id: anonId },
    });
    setData(res.data);
  };

  const onUploadClick = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !event) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const upRes = await axios.post(
        `${API}/events/${event.event_id}/photos?source=challenge`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      const photo = upRes.data;
      const path = photo?.storage_path || '';
      const mediaUrl = path ? getMediaUrl(path, `${API}/photos/${path}`) : '';
      const isVideo = (photo?.media_type === 'video');
      await axios.post(`${API}/challenges/${challengeId}/submit`, {
        media_url: mediaUrl,
        type: isVideo ? 'video' : 'photo',
        anon_id: anonId,
      });
      toast.success("🔥 You're in — check your rank");
      await refresh();
      // Auto-scroll to leaderboard
      setTimeout(() => leaderboardTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    } catch (err) {
      toast.error('Could not submit — please try again');
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onVote = async (entry) => {
    if (data?.viewer_voted_entry_id) {
      toast('You already voted on this challenge');
      return;
    }
    if (entry.is_current_user_entry) {
      toast('You can\'t vote for your own entry');
      return;
    }
    try {
      const res = await axios.post(`${API}/challenges/${challengeId}/vote`, {
        entry_id: entry.entry_id,
        anon_id: anonId,
      });
      if (res.data?.already_voted) {
        toast('You already voted');
      } else {
        toast.success('✅ Voted');
      }
      await refresh();
    } catch (err) {
      toast.error('Could not vote — please try again');
    }
  };

  const onShare = async () => {
    const url = `${window.location.origin}/prom/${slug}/challenges/${challengeId}`;
    const text = `Vote for me 👑 ${url}`;
    trackGrowth('challenge_share', { metadata: { challenge_id: challengeId } });
    try {
      if (navigator.share) {
        await navigator.share({ title: data?.challenge?.title || 'Prom Challenge', text, url });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success('Link copied — paste in your group chat to get votes');
      }
    } catch {
      // user cancelled — silent
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center" data-testid="prom-leaderboard-loading">
        <Sparkle size={28} weight="fill" className="text-[#C29D52] animate-pulse" />
      </div>
    );
  }
  if (error || !data) {
    return <div className="min-h-screen bg-neutral-950 text-white p-6 text-center" data-testid="prom-leaderboard-error">{error || 'Not found'}</div>;
  }

  const entries = data.entries || [];
  const myEntry = entries.find((e) => e.is_current_user_entry);
  const top = entries[0];

  // Rank-context micro-prompt
  let ctxLine = null;
  if (entries.length === 0) ctxLine = 'Be the first 🔥';
  else if (myEntry && myEntry.rank === 1) ctxLine = "👑 You're currently winning";
  else if (myEntry && top && (top.votes - myEntry.votes) > 0 && (top.votes - myEntry.votes) <= 2) {
    ctxLine = `🔥 Only ${top.votes - myEntry.votes} vote${top.votes - myEntry.votes === 1 ? '' : 's'} behind #1`;
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white px-5 py-6" data-testid="prom-leaderboard-page">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(`/prom/${slug}/challenges`)}
          className="inline-flex items-center gap-1.5 text-[12px] text-white/55 hover:text-white mb-4"
          data-testid="prom-leaderboard-back"
        >
          <ArrowLeft size={12} weight="bold" /> All challenges
        </button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200 text-[10px] font-black uppercase tracking-[0.18em] mb-3">
            <Trophy size={11} weight="fill" /> {data.challenge.category}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mb-2" data-testid="prom-leaderboard-title">
            {data.challenge.title}
          </h1>
          {ctxLine && (
            <p className="text-sm text-white/75 mb-4" data-testid="prom-leaderboard-context">
              {ctxLine}
            </p>
          )}
        </motion.div>

        {/* Submit CTA */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          capture="environment"
          className="hidden"
          onChange={onFileSelected}
          data-testid="prom-leaderboard-file-input"
        />
        <button
          onClick={onUploadClick}
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-white shadow-lg disabled:opacity-50 mb-3"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
          data-testid="prom-leaderboard-submit"
        >
          <Camera size={16} weight="fill" /> {submitting ? 'Uploading…' : (myEntry ? 'Submit another entry' : 'Submit your entry')}
        </button>

        <button
          onClick={onShare}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white border border-white/15 bg-white/[0.04] hover:bg-white/[0.08] transition-colors mb-6"
          data-testid="prom-leaderboard-share"
        >
          <ShareNetwork size={14} weight="fill" /> Get more votes
        </button>

        {/* Leaderboard */}
        <div ref={leaderboardTopRef}>
          <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-white/55 mb-3 flex items-center gap-1.5">
            <Flame size={14} weight="fill" className="text-amber-300" /> Leaderboard
          </h2>

          {entries.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center text-sm text-white/55" data-testid="prom-leaderboard-empty">
              No entries yet — be the first 🔥
            </div>
          ) : (
            <ol className="space-y-2" data-testid="prom-leaderboard-list">
              {entries.map((e) => {
                const voted = data.viewer_voted_entry_id === e.entry_id;
                const isMine = e.is_current_user_entry;
                return (
                  <li
                    key={e.entry_id}
                    className={`rounded-xl p-3 flex items-center gap-3 border ${
                      isMine ? 'bg-[#B89762]/30/10 border-[#B89762]/40/30'
                      : e.rank === 1 ? 'bg-amber-300/8 border-amber-300/25'
                      : 'bg-white/[0.04] border-white/8'
                    }`}
                    data-testid={`prom-leaderboard-row-${e.rank}`}
                  >
                    <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                      e.rank === 1 ? 'bg-amber-300/25 text-amber-200'
                      : e.rank === 2 ? 'bg-white/15 text-white/85'
                      : e.rank === 3 ? 'bg-orange-400/20 text-orange-200'
                      : 'bg-white/[0.06] text-white/55'
                    }`}>
                      {e.rank === 1 ? <Crown size={14} weight="fill" /> : `#${e.rank}`}
                    </div>
                    {e.media_url ? (
                      e.type === 'video' ? (
                        <video src={e.media_url} className="w-12 h-12 rounded-lg object-cover bg-neutral-900" muted />
                      ) : (
                        <img src={e.media_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-neutral-900" />
                      )
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-white/[0.04]" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">
                        {isMine ? 'Your entry' : `Entry #${e.rank}`}
                      </p>
                      <p className="text-[11px] text-white/55">
                        {e.votes} vote{e.votes === 1 ? '' : 's'}
                      </p>
                    </div>
                    <button
                      onClick={() => onVote(e)}
                      disabled={!!data.viewer_voted_entry_id || isMine}
                      className={`shrink-0 px-3 py-2 rounded-lg text-[12px] font-black transition-colors ${
                        voted ? 'bg-emerald-400/20 text-emerald-200'
                        : data.viewer_voted_entry_id || isMine ? 'bg-white/[0.04] text-white/35 cursor-not-allowed'
                        : 'bg-white text-black hover:bg-neutral-200'
                      }`}
                      data-testid={`prom-leaderboard-vote-${e.rank}`}
                    >
                      {voted ? <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Voted</span>
                        : isMine ? 'Your entry'
                        : 'Vote'}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* Upsell — visible after the user has engaged with leaderboard */}
        <UpsellBanner source="prom_leaderboard" variant="card" />
      </div>
    </div>
  );
}
